import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/logomiestampita.png';
import oliveBranch from '../assets/landing/olivo-miestampitacom.png';
import cintaImage from '../assets/landing/cinta-miestampitacom.png';
import sobreImage from '../assets/landing/sobre-miestampitacom.png';
import estampitaImage from '../assets/landing/estampita-miestampitacom.png';
import angelitosImage from '../assets/landing/angelitos-miestampitacom.png';
import ramaImage from '../assets/landing/rama-miestampitacom.png';
import osoImage from '../assets/landing/oso-miestampitacom.png';
import useSEO from '../hooks/useSEO';
import './Landing.css';

const STEPS = [
  {
    image: angelitosImage,
    title: 'ELEGÍ',
    description: 'Explorá la colección.',
  },
  {
    image: ramaImage,
    title: 'PERSONALIZÁ',
    description: 'Completá los datos de tu celebración.',
  },
  {
    image: osoImage,
    title: 'RECIBILAS',
    description: 'Las imprimimos y las enviamos a tu casa.',
  },
];

const Landing = () => {
  const stepsRef = useRef(null);

  useSEO({
    title: 'MiEstampita | Estampitas para Bautismo y Primera Comunión',
    description:
      'Estampitas delicadas para bautismo y primera comunión. Diseños personalizados en acuarela, hechos a mano en Montevideo, Uruguay, con envíos a Punta del Este y todo el país.',
    path: '/',
  });

  // Only meaningful on mobile, where CSS turns .landing-page__steps into a
  // horizontally scrolling, swipeable carousel (one step per screen width).
  // Desktop keeps its normal wrapping row and never renders/uses this arrow.
  //
  // A clone of the first slide is appended after RECIBILAS so "next" past
  // the last real slide keeps moving forward onto that clone instead of
  // sweeping backwards across the whole track. Once the scroll settles on
  // the clone, we snap (no animation) back to the real first slide, which
  // looks identical, so the loop reads as seamless.
  const handleNextStep = () => {
    const container = stepsRef.current;
    if (!container) return;

    const slideWidth = container.clientWidth;
    container.scrollTo({
      left: container.scrollLeft + slideWidth,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const container = stepsRef.current;
    if (!container) return undefined;

    let idleTimeout;

    const handleScroll = () => {
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => {
        const isOnClone = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
        if (isOnClone) {
          container.scrollTo({ left: 0, behavior: 'auto' });
        }
      }, 120);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(idleTimeout);
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="landing-page">
      <div className="landing-page__hero">
        <img
          src={cintaImage}
          alt=""
          aria-hidden="true"
          className="landing-page__corner landing-page__corner--left"
        />
        <img
          src={oliveBranch}
          alt=""
          aria-hidden="true"
          className="landing-page__corner landing-page__corner--right"
        />

        <img
          src={sobreImage}
          alt=""
          aria-hidden="true"
          className="landing-page__side-image landing-page__side-image--left"
        />
        <img
          src={estampitaImage}
          alt=""
          aria-hidden="true"
          className="landing-page__side-image landing-page__side-image--right"
        />

        <div className="landing-page__content">
          <img src={logo} alt="MiEstampita" className="landing-page__logo" />

          <h1 className="landing-page__headline">
            La forma más simple <span className="landing-page__no-orphan">de tener</span>
            <br />
            tus estampitas impresas
          </h1>

          <p className="landing-page__paragraph">
            Ilustraciones originales,{' '}
            <span className="landing-page__no-orphan">pintadas a mano para</span>{' '}
            <span className="landing-page__no-orphan">bautismos y comuniones.</span>
            <br />
            Elegí tu diseño, personalizalo y recibilo listo para celebrar.
          </p>

          <Link to="/coleccion" className="landing-page__cta">
            Elegir un diseño
          </Link>
        </div>
      </div>

      <hr className="landing-page__divider" />

      <div className="landing-page__steps-wrapper">
        <div className="landing-page__steps" ref={stepsRef}>
          {STEPS.map((step) => (
            <div className="landing-page__step" key={step.title}>
              <div className="landing-page__step-image-wrapper">
                <img
                  src={step.image}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="landing-page__step-image"
                />
              </div>
              <h3 className="landing-page__step-title">{step.title}</h3>
              <p className="landing-page__step-description">{step.description}</p>
            </div>
          ))}
          <div className="landing-page__step landing-page__step--clone" aria-hidden="true">
            <div className="landing-page__step-image-wrapper">
              <img
                src={STEPS[0].image}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="landing-page__step-image"
              />
            </div>
            <h3 className="landing-page__step-title">{STEPS[0].title}</h3>
            <p className="landing-page__step-description">{STEPS[0].description}</p>
          </div>
        </div>

        <button
          type="button"
          className="landing-page__steps-arrow"
          onClick={handleNextStep}
          aria-label="Ver siguiente paso"
        >
          ›
        </button>
      </div>
    </div>
  );
};

export default Landing;
